import React, { useState, useEffect, useCallback } from 'react';
import {
  PhoneIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { agentService, AgentCallActivity } from '../../services/agentService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/UI/Card';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import toast from 'react-hot-toast';

const PAGE_SIZE = 20;

const normalizeCallDisposition = (reason?: string): string | null => {
  if (!reason) return null;
  const normalized = reason.toLowerCase().replace(/_/g, ' ').trim();
  if (normalized === 'not interested') return 'not_interested';
  if (normalized === 'follow up') return 'follow_up';
  if (normalized === 'no answer / voicemail' || normalized === 'voicemail') return 'voicemail';
  if (normalized === 'no answer') return 'no_answer';
  if (normalized === 'busy') return 'busy';
  if (normalized === 'wrong number') return 'wrong_number';
  if (normalized === 'gatekeeper') return 'gatekeeper';
  if (normalized === 'operator') return 'operator';
  if (normalized === 'answered') return 'answered';
  return null;
};

const AgentCallHistoryPage: React.FC = () => {
  const [calls, setCalls] = useState<AgentCallActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const data = await agentService.getMyCallsHistory({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setCalls(data.calls);
      setTotalCount(data.pagination.total);
      setTotalPages(data.pagination.totalPages);
    } catch (err: any) {
      console.error('Failed to fetch call history:', err);
      toast.error('Failed to load call history');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  // Debounce search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleStatusChange = (s: string) => {
    setStatusFilter(s);
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDuration = (secs?: number) => {
    if (!secs) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Call History</h1>
        <p className="text-sm text-muted-foreground mt-1">
          View all your call attempts and their outcomes.
        </p>
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="input pl-10"
                  placeholder="Search by lead name, phone, or campaign..."
                />
              </div>
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outlined"
              size="sm"
              className="ml-4"
            >
              <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Filter
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="select"
                  >
                    <option value="">All Calls</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="no_answer">No Answer</option>
                    <option value="voicemail">Voicemail</option>
                    <option value="busy">Busy</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Calls Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : calls.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <PhoneIcon className="h-8 w-8 text-primary" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-foreground">No calls found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {search || statusFilter ? 'No calls match your current filters.' : 'No call history yet.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Lead</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Campaign</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Call Status</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Result</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Lead Status</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Duration</th>
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <tr key={call.leg_id} className="border-b border-border/50 hover:bg-accent/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-medium text-foreground">{call.lead_name || 'Unknown'}</div>
                      <div className="text-xs text-muted-foreground">{call.lead_phone || '—'}</div>
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{call.campaign_name || '—'}</td>
                    <td className="py-3 px-4">
                      <StatusBadge status={call.call_status || call.leg_status} type="call" />
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const disposition = normalizeCallDisposition(call.call_reason);
                        if (disposition) {
                          return <StatusBadge status={disposition} type="result" />;
                        }
                        if (call.call_reason) {
                          return (
                            <span className="text-xs text-muted-foreground">{call.call_reason}</span>
                          );
                        }
                        return <span className="text-xs text-muted-foreground">—</span>;
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      {call.lead_status ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          call.lead_status === 'interested'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : call.lead_status === 'not_interested'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            : call.lead_status === 'follow_up'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            : call.lead_status === 'no_answer'
                            ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {call.lead_status
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDuration(call.duration_seconds)}</td>
                    <td className="py-3 px-4 text-muted-foreground text-xs">
                      {new Date(call.routed_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, totalCount)} of {totalCount.toLocaleString()}
          </p>
          <div className="flex items-center space-x-2">
            <Button variant="outlined" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              <ChevronLeftIcon className="h-4 w-4 mr-1" /> Previous
            </Button>
            {(() => {
              const pages: number[] = [];
              const max = 5;
              let start = Math.max(1, page - Math.floor(max / 2));
              let end = Math.min(totalPages, start + max - 1);
              if (end - start + 1 < max) start = Math.max(1, end - max + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map(p => (
                <button
                  key={p}
                  onClick={() => handlePageChange(p)}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    p === page
                      ? 'bg-primary text-primary-foreground font-medium'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {p}
                </button>
              ));
            })()}
            <Button variant="outlined" size="sm" disabled={page >= totalPages} onClick={() => handlePageChange(page + 1)}>
              Next <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCallHistoryPage;
